/**
 * The Help Centre's content, held once.
 *
 * This module is the source of truth for how DealPulse explains itself. It is
 * data, not markup, so the same text can be rendered in more than one place
 * without being retyped: the `/help` screen renders all of it today, and any
 * inline explainer that arrives later reads the section it needs from here.
 *
 * **Nothing in here restates a vocabulary the product already owns.** Signal,
 * stage and recommendation names come from `features/pipeline/labels.ts`, the
 * same dictionary the cards and the panel read, so the help text cannot call a
 * signal something the board does not.
 *
 * How much the compiler actually guarantees differs by vocabulary, and it is
 * worth being precise. `Stage` and `Recommendation` are generated unions, so a
 * renamed backend value breaks this build. `SignalKey` and `RiskKey` are not:
 * the backend types those response fields as open dictionaries, so the generated
 * type widens to `string` and the maps below are open records. For those two,
 * `content.test.ts` is the protection — it asserts every label has a meaning, at
 * test time rather than compile time. Named, not assumed.
 *
 * Four claims inherited from the old copy were wrong against v2 and are fixed
 * here: the upload button's name, the file types accepted, auto-archive (which
 * did not exist and now does), and the Reports sections and their gate.
 */

import type { Recommendation, RiskKey, SignalKey, Stage } from '../../api/types'
import {
  PROGRESSION_SIGNALS,
  QUALIFICATION_SIGNALS,
  RECOMMENDATION_LABEL,
  RISK_LABEL,
  SIGNAL_LABEL,
  STAGE_LABEL,
} from '../pipeline/labels'

export type HelpItem = { term?: string; body: string }

export type HelpBlock =
  | { kind: 'text'; body: string }
  | { kind: 'list'; items: readonly HelpItem[]; ordered?: boolean }
  | { kind: 'note'; body: string }

export type HelpSection = {
  /** Stable across edits: it is the anchor an inline "explain this" would link to. */
  id: string
  title: string
  blocks: readonly HelpBlock[]
}

export const HELP_INTRO =
  'YourDealPulse reads your sales-call transcripts and tells you which deals are worth your time, and why. ' +
  'Every conclusion is grounded in what was actually said on the call. It does not guess, score or predict. ' +
  'If the words are not there, YourDealPulse will not claim it.'

/** What each qualification and progression signal means. Completeness is tested. */
export const SIGNAL_MEANING: Record<SignalKey, string> = {
  problem_confirmed: 'a real business problem the prospect confirmed.',
  operational_impact: 'that problem is hurting operations in a concrete way.',
  budget_allocated: 'money is confirmed to be available for it.',
  decision_authority_present: 'someone who can actually decide is involved.',
  buying_process_active: 'an active buying process is under way.',
  internal_momentum: "the prospect's own side is pushing this forward.",
  next_step_defined: 'a concrete next step was agreed.',
  solution_presented: 'you have put a specific solution in front of them, not just talked in the abstract.',
  pricing_discussed: 'real numbers for this initiative have been talked about, not a vague ballpark.',
  commercial_objection: 'they have pushed back on price or terms.',
  contract_engaged: 'a contract or order for this work is genuinely in motion, not just an NDA or early paperwork.',
  timeline_committed: 'a real date or deadline has been agreed.',
}

export const STAGE_MEANING: Record<Stage, string> = {
  no_opportunity: 'the client said there is nothing to solve or nothing to fund; this is stated on a call, never assumed from what we have not confirmed yet.',
  early_exploration: 'a problem is surfacing; still learning.',
  active_scoping: 'working through what a solution looks like.',
  proposal: 'a proposal is on the table.',
  negotiation: 'terms, pricing and commercials are being agreed.',
  qualified: 'the strongest position: a real, moving, decision-ready deal.',
  blocked: 'the calls surfaced something that has genuinely killed it. See blockers below.',
}

export const RECOMMENDATION_MEANING: Record<Recommendation, string> = {
  keep_investing:
    'a confirmed decision-maker, a real problem with operational impact, and the deal is moving. Worth your time.',
  pause: 'not enough is confirmed yet, or the deal has gone quiet. Not dead, but not earning your attention right now. Revisit it.',
  stop_investing: 'the calls themselves show the deal is dead.',
}

export const RISK_MEANING: Record<RiskKey, string> = {
  vendor_saturation: 'a competing vendor is in the picture.',
  vendor_selected: 'the prospect already leans towards another vendor.',
  implementation_skepticism: 'there is doubt about whether a solution will actually work.',
  direction_locked: 'a direction was locked in before you arrived.',
  internal_bias: 'someone inside has a strong preference of their own.',
}

/** The ladder, least to most advanced. "blocked" sits outside it and is listed separately. */
const STAGE_LADDER: readonly Stage[] = [
  'no_opportunity',
  'early_exploration',
  'active_scoping',
  'proposal',
  'negotiation',
  'qualified',
]

const RECOMMENDATION_ORDER: readonly Recommendation[] = ['keep_investing', 'pause', 'stop_investing']

const RISK_ORDER: readonly RiskKey[] = [
  'vendor_saturation',
  'vendor_selected',
  'implementation_skepticism',
  'direction_locked',
  'internal_bias',
]

/**
 * Sentence case: the dictionary stores signal labels lower case for use inside a
 * badge, and a definition list wants them capitalised.
 *
 * The `?? key` fallbacks are not defensive noise. `SignalKey` widens to `string`
 * (see the note at the top), so the compiler cannot prove these lookups hit —
 * the same reason `features/deals/SignalEvidence.tsx` does it. Showing the raw
 * key beats rendering `undefined`, and the test asserts it never comes to that.
 */
const sentenceCase = (text: string) => text.charAt(0).toUpperCase() + text.slice(1)

const signalItems = (keys: readonly SignalKey[]): HelpItem[] =>
  keys.map((key) => ({
    term: sentenceCase(SIGNAL_LABEL[key] ?? String(key)),
    body: SIGNAL_MEANING[key] ?? '',
  }))

export const HELP_SECTIONS: readonly HelpSection[] = [
  {
    id: 'getting-started',
    title: 'Getting started',
    blocks: [
      {
        kind: 'text',
        body:
          'To begin, upload the transcript of one sales call. Use **+ New Transcript** at the top of the Pipeline, ' +
          'choose the company and country the call belongs to, and drop in a **PDF, DOCX or TXT** file, up to 10 MB. ' +
          'YourDealPulse analyses that one conversation and gives you a first read straight away. That is the whole first ' +
          'step: one call in, a straight answer out. From there you add follow-up calls and the deal builds over time.',
      },
    ],
  },
  {
    id: 'calls-and-deals',
    title: 'The two building blocks: calls and deals',
    blocks: [
      { kind: 'text', body: 'These two words mean something specific in YourDealPulse, so it is worth getting them straight first.' },
      {
        kind: 'text',
        body:
          '**A call** is one conversation with a prospect. You upload its transcript and YourDealPulse analyses that ' +
          'single conversation on its own.',
      },
      {
        kind: 'text',
        body:
          '**A deal** is one opportunity with a company, built up from all of its calls. When you upload a follow-up ' +
          'call, YourDealPulse attaches it to the same deal, so the picture accumulates over time rather than starting ' +
          'fresh each time. One deal, many calls.',
      },
    ],
  },
  {
    id: 'what-happens',
    title: 'What happens when you upload a transcript',
    blocks: [
      {
        kind: 'text',
        body:
          'YourDealPulse reads the transcript and produces two things. A **Call Summary** covers only that one ' +
          'conversation: what happened, what is new, what changed, what was agreed. A **Deal Summary** is the running ' +
          'story of the whole deal across every call. The Call Summary is the latest chapter; the Deal Summary is the book.',
      },
    ],
  },
  {
    id: 'follow-up-calls',
    title: 'Adding follow-up calls',
    blocks: [
      {
        kind: 'text',
        body:
          'When you have another call with a company you have already loaded, open that deal and use **Add a call ' +
          'transcript**. YourDealPulse attaches the call to the same deal and carries over the company and country for ' +
          'you, so everything stays on one timeline. If you upload from **+ New Transcript** instead and YourDealPulse ' +
          'spots a likely match, it asks whether to attach the call to the existing deal or start a new one, so a ' +
          'follow-up never quietly becomes a duplicate.',
      },
    ],
  },
  {
    id: 'signals',
    title: 'Signals: what we look for in a call',
    blocks: [
      {
        kind: 'text',
        body:
          'YourDealPulse looks for seven signals, and confirms each one only when the transcript explicitly shows it. ' +
          'A signal is never assumed from tone or job title.',
      },
      { kind: 'list', items: signalItems(QUALIFICATION_SIGNALS) },
    ],
  },
  {
    id: 'progression-signals',
    title: 'Progression signals: how far it has moved',
    blocks: [
      {
        kind: 'text',
        body:
          'The seven signals above tell you whether a deal is real. A second, smaller set tells you how far it has ' +
          'actually moved, and YourDealPulse confirms each one only when the transcript shows it:',
      },
      { kind: 'list', items: signalItems(PROGRESSION_SIGNALS) },
      {
        kind: 'text',
        body:
          'These show as a separate group on the deal, and they are what carry it up the later stages: proposal, ' +
          'negotiation and beyond.',
      },
    ],
  },
  {
    id: 'ask-the-questions',
    title: 'Getting the most from it: ask the questions',
    blocks: [
      {
        kind: 'text',
        body:
          'YourDealPulse can only confirm what the conversation actually contains. When a signal is missing, it usually ' +
          'means the question was not asked, not that the answer was no. So the analysis is only ever as good as your ' +
          'discovery. On the call, draw out the problem and what it is costing, find out whether budget exists and who ' +
          'really decides, understand how they buy, and agree a concrete next step. Ask well and the signals appear on ' +
          'their own. It helps to think of YourDealPulse as a mirror held up to your calls: it rewards a real ' +
          'conversation, and quietly exposes a thin one.',
      },
    ],
  },
  {
    id: 'stages',
    title: 'Stages: how far the conversation has moved',
    blocks: [
      {
        kind: 'text',
        body:
          'A stage reflects how far the conversation itself has progressed, inferred only from the calls, never from ' +
          'a date or field in a CRM. Deals move up this ladder as the signals accumulate:',
      },
      {
        kind: 'list',
        ordered: true,
        items: STAGE_LADDER.map((stage) => ({ term: STAGE_LABEL[stage], body: STAGE_MEANING[stage] })),
      },
      { kind: 'text', body: `A deal is marked **${STAGE_LABEL.blocked}** instead if ${STAGE_MEANING.blocked}` },
    ],
  },
  {
    id: 'blockers',
    title: 'Blockers: what holds a deal back',
    blocks: [
      {
        kind: 'text',
        body:
          'Where signals move a deal forward, a **blocker** is a fact from the calls that stands in its way. ' +
          'YourDealPulse records one only when the transcript actually states it, never on a hunch, and it stamps the call ' +
          'the blocker first appeared on, so you can see how long a deal has been carrying it.',
      },
      { kind: 'text', body: 'Blockers come in two strengths, and this is what decides Pause versus Stop:' },
      {
        kind: 'list',
        items: [
          {
            term: 'Freezing',
            body:
              'the deal cannot move right now but is not lost: a budget or decision freeze, an acquisition in ' +
              `progress, waiting on regulatory approval, or simply the wrong timing. This holds the deal at ` +
              `${RECOMMENDATION_LABEL.pause}.`,
          },
          {
            term: 'Deal-ending',
            body:
              'the fact kills the deal: no real need, no budget at all, or the prospect is locked to another vendor. ' +
              `This moves the deal to ${RECOMMENDATION_LABEL.stop_investing}.`,
          },
        ],
      },
      {
        kind: 'text',
        body:
          'This is how YourDealPulse stops a dead deal from quietly looking healthy: one confirmed deal-ending fact ' +
          'outweighs any number of positive signals. As always, the blocker must be evidenced in the transcript, and ' +
          'the exact quote is kept so you can check it.',
      },
    ],
  },
  {
    id: 'risks',
    title: 'Risks: what a deal is carrying',
    blocks: [
      {
        kind: 'text',
        body:
          'Alongside blockers, YourDealPulse notes softer **risks** a deal is carrying: things from the calls that do not ' +
          'stop a deal but are worth keeping an eye on.',
      },
      {
        kind: 'list',
        items: RISK_ORDER.map((risk) => ({ term: RISK_LABEL[risk] ?? String(risk), body: RISK_MEANING[risk] ?? '' })),
      },
      {
        kind: 'text',
        body:
          `A risk does not move a deal to ${RECOMMENDATION_LABEL.stop_investing} the way a deal-ending blocker does. ` +
          'It is context, surfaced so nothing important stays buried, and like everything else it is taken only from ' +
          'what the transcript says.',
      },
    ],
  },
  {
    id: 'recommendation',
    title: 'The recommendation: Keep, Pause or Stop',
    blocks: [
      { kind: 'text', body: 'For each deal YourDealPulse gives one plain recommendation:' },
      {
        kind: 'list',
        items: RECOMMENDATION_ORDER.map((r) => ({ term: RECOMMENDATION_LABEL[r], body: RECOMMENDATION_MEANING[r] })),
      },
      {
        kind: 'note',
        body:
          'YourDealPulse reasons only over what is in the transcripts. It will never overreach to a Stop, because your ' +
          'real reasons to stop often live outside the call. Its job is to make sure a dead or stalling deal never ' +
          'quietly hides on your board. The final call to stop is always yours.',
      },
    ],
  },
  {
    id: 'decision-maker',
    title: 'People and the decision-maker',
    blocks: [
      {
        kind: 'text',
        body:
          'YourDealPulse detects who was on each call, then asks you to confirm, for each person, whether they can ' +
          'actually make the decision: yes, no, or not sure. Authority is your judgement, not something guessed from ' +
          `a title. A confirmed decision-maker is what lets a deal qualify as a ${RECOMMENDATION_LABEL.keep_investing}, ` +
          'so keeping this accurate matters. You edit people in **Edit lead details** on any deal.',
      },
    ],
  },
  {
    id: 'lead-details',
    title: 'Editing lead details',
    blocks: [
      {
        kind: 'text',
        body:
          'Every deal carries details you own: the company name, the country (required when the deal is created), and ' +
          'the people on the calls with their decision-maker status. Open **Edit lead details** on any deal to change ' +
          'them. If a company was named oddly, a country needs setting, or a person’s authority needs confirming, ' +
          'this is where you put it right, and your version is the one YourDealPulse uses from then on.',
      },
    ],
  },
  {
    id: 'outcome',
    title: 'Recording the outcome: Won or Lost',
    blocks: [
      {
        kind: 'text',
        body:
          'Keep, Pause and Stop are the live verdict while a deal is in play. When a deal actually closes, record the ' +
          'outcome: mark it **Won** or **Lost**. Closing often happens off the call, so YourDealPulse leaves this to you ' +
          'rather than guessing. A recorded outcome moves the deal out of your active pipeline and feeds the Reports, ' +
          'so the numbers reflect what really happened.',
      },
    ],
  },
  {
    id: 'archiving',
    title: 'Archiving',
    blocks: [
      {
        kind: 'text',
        body:
          'Archiving takes a deal off your active board without deleting anything. You can archive any deal yourself ' +
          'at any time, and recording an outcome archives it for you. Turn on **Show archived** in Pipeline to see ' +
          'them, and restore any of them to the active board.',
      },
      {
        kind: 'text',
        body:
          'You can also have it happen on its own. In **Settings → Auto-archive**, choose how long a deal you have ' +
          'marked Stop investing stays on the board before it is archived: 30, 60 or 90 days, or Never to switch it ' +
          'off. The clock starts when you mark the deal Stop investing, not when the deal was created, and only your ' +
          'own decision counts. A deal you restore by hand is never auto-archived again.',
      },
    ],
  },
  {
    id: 'reports',
    title: 'Reports: your whole pipeline at a glance',
    blocks: [
      {
        kind: 'text',
        body:
          'Where a deal answers "is this one worth it?", Reports answer "how healthy is the whole pipeline?". ' +
          'Reports become available once you have 20 analysed calls, because below that there is not enough evidence ' +
          'for the figures to mean anything. Until then the section is not shown.',
      },
      {
        kind: 'list',
        ordered: true,
        items: [
          { term: 'Pipeline Performance', body: 'how the pipeline is doing, before it says where it hurts.' },
          { term: 'Immediate Actions', body: "what to do now: YourDealPulse's own verdicts first, observations second." },
          { term: 'Pipeline Quality', body: 'how much of what you are carrying is genuinely real.' },
        ],
      },
      {
        kind: 'text',
        body:
          'Every card can be opened to show the opportunities it refers to. As with everything else, every figure ' +
          'traces back to what was said on calls. No scores, no probabilities.',
      },
    ],
  },
  {
    id: 'you-are-in-control',
    title: 'You are in control, correct anything',
    blocks: [
      {
        kind: 'text',
        body:
          'YourDealPulse reads the calls, but you decide. If a verdict, a stage or a person looks wrong, change it. Your ' +
          'corrections are treated as the truth. One trusted deal at a time is how the whole picture stays trustworthy.',
      },
    ],
  },
  {
    id: 'credits',
    title: 'Credits',
    blocks: [
      {
        kind: 'text',
        body:
          'Analysing one call uses one credit. Uploading a follow-up call on an existing deal is another analysis, and ' +
          'so another credit. Your remaining balance is shown in **Settings**, under Plan & usage.',
      },
    ],
  },
  {
    // Every claim here is the Terms of Service in plainer words, and nothing
    // more: refunds, cancelling, what happens to credits on deletion, and the
    // one case where a credit comes back. If the Terms change, this changes.
    id: 'refunds',
    title: 'Refunds and cancelling',
    blocks: [
      {
        kind: 'text',
        body:
          '**Fees are non-refundable.** Cancelling stops the next payment rather than returning the last one: you keep ' +
          'the month you have already paid for, and your account, your history and any packs you hold stay with you ' +
          'afterwards. You can cancel from inside YourDealPulse at any time.',
      },
      {
        kind: 'text',
        body:
          'Packs never expire while your account exists, whether or not you subscribe. If you ask us to delete your ' +
          'account, the credits you still hold stay available during the 30-day recovery period, and expire when the ' +
          'deletion becomes permanent.',
      },
      {
        kind: 'note',
        body:
          'One case does return a credit, and it is automatic: if an analysis fails, the credit goes back to the pot it ' +
          'came from, whatever the cause. We do not classify the fault, and you do not have to ask.',
      },
    ],
  },
  {
    id: 'your-data',
    title: 'Your data, privacy and consent',
    blocks: [
      {
        kind: 'text',
        body:
          'Your transcripts are processed only to produce your own analysis, and are never shared with other users. ' +
          'Everything runs on EU-hosted infrastructure. The uploaded file is removed once it has been analysed; the ' +
          'summaries and signals it produces are saved to your workspace. Analysis is generated by an AI model working ' +
          'strictly from the transcript you provide, and that provider does not use your data to train its models.',
      },
      {
        kind: 'text',
        body:
          'You stay in control of your data: you can export it to CSV at any time from Settings, and if you close your ' +
          'account it enters a 30-day deletion window, after which your personal and application data is deleted or ' +
          'anonymised.',
      },
      {
        kind: 'note',
        body:
          'One responsibility sits with you: you must have the right to record and upload these calls, including any ' +
          'consent the people on them need under the laws that apply to you. YourDealPulse analyses what you give it; ' +
          'making sure you were allowed to capture it is your call.',
      },
    ],
  },
]
